import { DashboardCard } from '@nexus/ari-lib/components/dashboard-card';
import { ArGlassesIcon } from '@nexus/ari-lib/assets/icons';
import { EmptyContent } from '@nexus/ari-lib/components/empty-content';
import { Iconify } from '@nexus/ari-lib/components/iconify';
import { solidItem } from '@nexus/ari-lib/utils/glass-styles';

import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { UiDeviceItem } from '../../hooks/use-devices';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDeviceImage } from '../devices/device-image';

// Rows keep their natural height and any leftover card space stays empty, so a
// card with one device doesn't render a single stretched row.
const MAX_ROWS = 3;

/**
 * Mirrors the STATUS pill on the devices page (raised container, 6px status dot)
 * without its caption — a dashboard row is too short to carry a label, and the
 * column reads as status from context. Sits on container.high rather than the
 * devices page's container.medium: the dashboard row is itself container.medium,
 * so the pill has to step up one elevation level to stay visible.
 */
function DeviceStatusPill({ status }: { status: UiDeviceItem['status'] }) {
  const isOnline = status === 'ONLINE';
  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexShrink: 0,
        bgcolor: theme.palette.container.high,
        borderRadius: 1.5,
        height: 28,
        px: 1.5,
      })}
    >
      <Box
        sx={(theme) => ({
          width: 6,
          height: 6,
          borderRadius: '50%',
          flexShrink: 0,
          bgcolor: isOnline ? theme.palette.success.main : theme.palette.warning.main,
        })}
      />
      <Typography sx={(theme) => ({ fontSize: 12, color: theme.palette.grey[50] })}>
        {isOnline ? 'Online' : 'Offline'}
      </Typography>
    </Box>
  );
}

type DashboardDevicesSectionProps = {
  userDevices: UiDeviceItem[];
  userDevicesLoading: boolean;
  userDevicesError: Error | null;
  onClick?: () => void;
  comingSoon?: boolean;
};

export function DashboardDevicesSection({
  userDevices,
  userDevicesError,
  userDevicesLoading,
  onClick,
  comingSoon = false,
}: DashboardDevicesSectionProps) {
  const { t: translate } = useTranslation();
  const itemStyle = solidItem;

  useEffect(() => {
    if (userDevicesError !== null) {
      console.error(userDevicesError.message);
    }
  }, [userDevicesError]);

  return (
    <DashboardCard
      data-testid="cloud.dashboard.devices-section"
      title="Device Status"
      subtitle="Connected AR Devices"
      icon={<ArGlassesIcon sx={{ width: 28, height: 28 }} />}
      onClick={onClick}
      comingSoon={comingSoon}
    >
      {userDevicesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : userDevicesError !== null ? (
        <EmptyContent
          title=""
          description={translate('devices.loadingFailed')}
          sx={{ px: 0, color: 'text.secondary' }}
          icon={<ArGlassesIcon sx={{ width: 40, height: 40, opacity: 0.4 }} />}
        />
      ) : userDevices.length === 0 ? (
        <EmptyContent
          title=""
          description={translate('devices.noDevicesConnected')}
          sx={{ px: 0, color: 'text.secondary' }}
          icon={<ArGlassesIcon sx={{ width: 40, height: 40, opacity: 0.4 }} />}
        />
      ) : (
        <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
          {userDevices.slice(0, MAX_ROWS).map((device) => (
            <Box
              key={device.serialNumber}
              sx={{
                px: 2,
                // Same row height as the Memory card's toggle row.
                height: 68,
                ...itemStyle,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {getDeviceImage(device) !== null ? (
                <Box
                  component="img"
                  src={getDeviceImage(device) as string}
                  alt={device.name || device.serialNumber}
                  width={256}
                  height={171}
                  sx={{
                    maxHeight: 30,
                    width: 'auto',
                    objectFit: 'contain',
                    mr: 1.5,
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Iconify
                  icon="solar:glasses-bold"
                  width={28}
                  sx={{ color: 'text.secondary', mr: 1.5, flexShrink: 0 }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={700} sx={{ display: 'block', lineHeight: 1.4, fontSize: 12 }}>
                  {device.model}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  {device.name || device.serialNumber}
                </Typography>
              </Box>
              <DeviceStatusPill status={device.status} />
            </Box>
          ))}
        </Stack>
      )}
    </DashboardCard>
  );
}
